import { encodeSecp256k1Signature } from '@cosmjs/amino';
import { makeSignBytes } from '@cosmjs/proto-signing';
import { providers } from 'ethers';

class Wallet {
  constructor(backend) {
    this.backend = backend;
  }
  generate() {
    return this.backend.generate();
  }
  loadMnemonic(mnemonic) {
    return this.backend.loadMnemonic(mnemonic);
  }
  unlock(password) {
    return this.backend.unlock(password);
  }
  lock() {
    return this.backend.lock();
  }
  erase() {
    return this.backend.erase();
  }
  getCosmosWallets(chainId, chainInfo) {
    return this.backend.getCosmosWallets(chainId, chainInfo);
  }
  getEthereumWallets(ethWsUrl) {
    return this.backend.getEthereumWallets(ethWsUrl);
  }
  cosmos(chainId, chainInfo) {
    return this.backend.cosmos(chainId, chainInfo);
  }
  ethereum(ethWsUrl) {
    return this.backend.ethereum(ethWsUrl);
  }
}

class WalletError extends Error {
}
class NotSupported extends WalletError {
}
class AddressNotFound extends WalletError {
}
const WalletErrors = {
  NotSupported,
  AddressNotFound
};

class WalletBackend {
  async generate() {
    throw new WalletErrors.NotSupported("Wallet.generate not supported");
  }
  async loadMnemonic(_mnemonic, _password) {
    throw new WalletErrors.NotSupported("Wallet.loadMnemonic not supported");
  }
  async unlock(_password) {
    return false;
  }
  async lock() {
  }
  async erase() {
  }
  getCosmosWallets(_chainId, _chainInfo) {
    return [];
  }
  getEthereumWallets(_ethWsUrl) {
    return [];
  }
  cosmos(_chainId, _chainInfo) {
    throw new WalletErrors.NotSupported("Cosmos wallet not supported");
  }
  ethereum(_ethWsUrl) {
    throw new WalletErrors.NotSupported("Ethereum wallet not supported");
  }
}

class AbstractWallet {
  getId() {
    return "unknown-wallet";
  }
  getDisplayName() {
    return "Unknown Wallet";
  }
  async connect() {
  }
  async disconnect() {
  }
  isAvailable() {
    return false;
  }
  getInstallUrl() {
    return "";
  }
  isConnected() {
    return false;
  }
}

class CosmosWallet extends AbstractWallet {
  constructor(chainId, chainInfo) {
    super();
    this.chainId = chainId;
    this.chainInfo = chainInfo;
  }
  getAccounts() {
    throw new WalletErrors.NotSupported("CosmosWallet.getAccounts not supported");
  }
  signBytes(_signerAddress, _bytes) {
    throw new WalletErrors.NotSupported("CosmosWallet.signBytes not supported");
  }
  async signDirect(signerAddress, signDoc) {
    const { pubkey = null } = (await this.getAccounts()).find(({ address }) => address === signerAddress) || {};
    if (!pubkey)
      throw new Error(`Can't find ${signerAddress}`);
    const signBytes = makeSignBytes(signDoc);
    const sig = await this.signBytes(signerAddress, signBytes);
    const signature = encodeSecp256k1Signature(pubkey, sig);
    return {
      signed: signDoc,
      signature
    };
  }
  getSigner() {
    if (!this.isConnected())
      return null;
    return this;
  }
  signTx() {
    throw new WalletErrors.NotSupported("CosmosWallet.signTx not supported");
  }
}

class EthereumWallet extends AbstractWallet {
  constructor(ethWsUrl) {
    super();
    this.ethWsUrl = ethWsUrl;
  }
  async getSigner() {
    throw new WalletErrors.NotSupported("EthereumWallet.getSigner not supported");
  }
  async getProvider() {
    if (this.wsProvider)
      return this.wsProvider;
    this.wsProvider = new providers.WebSocketProvider(this.ethWsUrl);
    return this.wsProvider;
  }
}

export { AbstractWallet, CosmosWallet, EthereumWallet, Wallet, WalletBackend, WalletErrors };
